import { format } from 'date-fns';
import { useEffectiveDateFormat } from 'hooks/useEffectiveDateFormat';
import { ReactNode } from 'react';
import { formatTimeOfDay } from 'utils/date';
import { t } from 'utils/getTranslations';
import { isNewDesignCopy } from 'utils/newDesignCopy';
import { BoxNowrap } from './BoxNoWrap';

type Props = {
    value: string | number | Date | null | undefined;
    fallback?: ReactNode;
    scopeCompanyId?: number | null;
    withTime?: boolean | string;
    nowrap?: boolean;
    /** Applies with `nowrap`, to the block that holds the date. */
    className?: string;
};

export function FormattedDate({ value, fallback = '-', scopeCompanyId, withTime, nowrap, className }: Props) {
    const effectiveFormat = useEffectiveDateFormat(scopeCompanyId);
    const text = dateText(value, effectiveFormat, withTime);
    if (text === null) return <>{fallback}</>;
    return nowrap ? <BoxNowrap className={className}>{text}</BoxNowrap> : <>{text}</>;
}

/** What `FormattedDate` writes, as a string, for a label that names the date. `null` for no date. */
export function useFormatDate(scopeCompanyId?: number | null) {
    const effectiveFormat = useEffectiveDateFormat(scopeCompanyId);
    return (value: Props['value'], withTime?: Props['withTime']) => dateText(value, effectiveFormat, withTime);
}

function dateText(value: Props['value'], effectiveFormat: string, withTime: Props['withTime']): string | null {
    if (value === null || value === undefined || value === '') return null;
    const date = value instanceof Date ? value : new Date(value);
    if (isNaN(date.getTime())) return null;
    if (withTime === true && isNewDesignCopy()) {
        // The new design's locale writes the time of day, and one message puts the date and the time together.
        return t('date-time', { date: format(date, effectiveFormat), time: formatTimeOfDay(date) });
    }
    const timePart = withTime === true ? 'HH:mm' : typeof withTime === 'string' ? withTime : null;
    return format(date, timePart ? `${effectiveFormat} ${timePart}` : effectiveFormat);
}
